import { authedFetch } from '../auth/supabase-browser';
import type {
  VoiceAgentSummary,
  VoiceAgentDetail,
  VoiceAgentsResponse,
  VoiceCallDetail,
  VoiceCallSummary,
  VoiceCallsResponse,
} from '@oneup/shared';

/**
 * Generic Client Portal module data client.
 *
 * Every list-style module shares the same Worker response envelope
 * ({ appId, tableKey, total, items, warnings, generatedAt }). This replaces the
 * per-module fetchers that AgentsPage/CallsPage previously hand-rolled.
 *
 * The browser never sends org/client identifiers — scope is derived from the
 * verified Supabase actor on the Worker. We only pass display filters.
 */

export interface ClientModuleRecord {
  id: string;
  createdTime: string;
  fields: Record<string, unknown>;
}

export interface ClientModuleWarning {
  code: string;
  message: string;
}

export interface ClientModuleListResponse {
  appId: string;
  tableKey: string;
  total: number;
  items: ClientModuleRecord[];
  warnings?: ClientModuleWarning[];
  nextCursor?: string | null;
  generatedAt: string;
}

export class ClientApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'ClientApiError';
  }
}

const API_BASE_URL = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL ?? ''
).replace(/\/$/, '');

export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export interface ClientModuleListParams {
  clientId: string;
  search?: string;
  filters?: Record<string, string | undefined>;
  limit?: number;
  cursor?: string | null;
}

export async function fetchClientModuleList(
  endpoint: string,
  expectedTableKey: string,
  params: ClientModuleListParams,
): Promise<ClientModuleListResponse> {
  const qs = new URLSearchParams();
  qs.set('client_id', params.clientId);
  if (params.search?.trim()) qs.set('search', params.search.trim());
  for (const [key, value] of Object.entries(params.filters ?? {})) {
    if (value && value.trim()) qs.set(key, value.trim());
  }
  qs.set('limit', String(params.limit ?? 100));
  if (params.cursor) qs.set('cursor', params.cursor);

  const url = `${apiUrl(endpoint)}${qs.toString() ? `?${qs.toString()}` : ''}`;
  const response = await authedFetch(url, { headers: { Accept: 'application/json' } });
  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ClientApiError(
      extractErrorMessage(body) ?? 'We couldn’t load that just now. Please try again in a moment.',
      response.status,
    );
  }

  const canonicalVoice = adaptCanonicalVoiceList(body, expectedTableKey);
  if (canonicalVoice) return canonicalVoice;

  if (!isListResponse(body, expectedTableKey)) {
    throw new ClientApiError(
      'We couldn’t load that just now. Please try again in a moment.',
      response.status,
    );
  }

  return body;
}

export async function fetchClientVoiceCallDetail(callId: string, clientId: string): Promise<ClientModuleRecord> {
  const query = new URLSearchParams({ client_id: clientId });
  const response = await authedFetch(apiUrl(`/api/voice/calls/${encodeURIComponent(callId)}?${query}`), {
    headers: { Accept: 'application/json' },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ClientApiError(
      extractErrorMessage(body) ?? 'We couldn’t load that call just now. Please try again in a moment.',
      response.status,
    );
  }
  if (!body || typeof body !== 'object' || !('item' in body)) {
    throw new ClientApiError('We couldn’t load that call just now. Please try again in a moment.', response.status);
  }
  return voiceCallRecord((body as { item: VoiceCallDetail }).item, clientId);
}

export async function fetchClientVoiceAgentDetail(
  agentId: string,
  clientId: string,
): Promise<VoiceAgentDetail> {
  const query = new URLSearchParams({ client_id: clientId });
  const response = await authedFetch(apiUrl(`/api/voice/agents/${encodeURIComponent(agentId)}?${query}`), {
    headers: { Accept: 'application/json' },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ClientApiError(
      extractErrorMessage(body) ?? 'We couldn’t load that agent just now. Please try again in a moment.',
      response.status,
    );
  }
  if (!body || typeof body !== 'object' || !('item' in body)) {
    throw new ClientApiError(
      'We couldn’t load that agent just now. Please try again in a moment.',
      response.status,
    );
  }
  return (body as { item: VoiceAgentDetail }).item;
}

export async function uploadClientVoiceAgentAvatar(
  agentId: string,
  clientId: string,
  file: File,
): Promise<{ avatarUrl: string }> {
  const query = new URLSearchParams({ client_id: clientId });
  const form = new FormData();
  form.set('file', file);
  const response = await authedFetch(
    apiUrl(`/api/voice/agents/${encodeURIComponent(agentId)}/avatar?${query}`),
    { method: 'PUT', body: form, headers: { Accept: 'application/json' } },
  );
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ClientApiError(
      extractErrorMessage(body) ?? 'We couldn’t upload that avatar. Please try again.',
      response.status,
    );
  }
  if (!body || typeof body !== 'object' || typeof (body as { avatarUrl?: unknown }).avatarUrl !== 'string') {
    throw new ClientApiError('The avatar uploaded, but the response was incomplete.', response.status);
  }
  return body as { avatarUrl: string };
}

export function adaptCanonicalVoiceList(
  value: unknown,
  expectedTableKey: string,
): ClientModuleListResponse | undefined {
  if (!value || typeof value !== 'object') return undefined;
  if (expectedTableKey === 'calls' && Array.isArray((value as Partial<VoiceCallsResponse>).items)) {
    const response = value as VoiceCallsResponse;
    return {
      appId: 'client-oneup',
      tableKey: 'calls',
      total: response.items.length,
      items: response.items.map((call) => voiceCallRecord(call)),
      warnings: [],
      nextCursor: response.nextCursor,
      generatedAt: new Date().toISOString(),
    };
  }
  if (expectedTableKey === 'agents' && Array.isArray((value as Partial<VoiceAgentsResponse>).items)) {
    const response = value as VoiceAgentsResponse;
    return {
      appId: 'client-oneup',
      tableKey: 'agents',
      total: response.items.length,
      items: response.items.map(voiceAgentRecord),
      warnings: response.configuration
        ? [{ code: response.configuration.status, message: response.configuration.message }]
        : [],
      generatedAt: new Date().toISOString(),
    };
  }
  return undefined;
}

export function voiceCallRecord(call: VoiceCallSummary | VoiceCallDetail, clientId?: string): ClientModuleRecord {
  const detail = call as Partial<VoiceCallDetail>;
  const scopedClientId = clientId ?? call.clientId;
  return {
    id: call.id,
    createdTime: call.startedAt ?? new Date(0).toISOString(),
    fields: compactFields({
      caller_full_name: call.caller.name,
      first_name: call.caller.firstName,
      last_name: call.caller.lastName,
      business_name: call.caller.businessName,
      phone_number: call.caller.phone,
      email_address: call.caller.email,
      Status: call.status,
      'Type Of Call': call.direction,
      started_at: call.startedAt,
      ended_at: call.endedAt,
      duration_minutes: call.durationMinutes,
      duration_seconds: call.durationSeconds,
      'Agent, Type': call.agentName,
      call_recording_url: detail.recordingUrl
        ? apiUrl(`${detail.recordingUrl}${scopedClientId ? `?client_id=${encodeURIComponent(scopedClientId)}` : ''}`)
        : call.recordingAvailable
          ? apiUrl(`/api/voice/calls/${encodeURIComponent(call.id)}/recording?client_id=${encodeURIComponent(scopedClientId)}`)
          : undefined,
      recording_available: call.recordingAvailable,
      call_summary: call.summary,
      call_trascript: detail.transcript,
      caller_requested_recap: call.followUpRequired,
      // Server-normalized analysis. Already client-safe: the closed
      // vocabularies carry no provider, cost, or cross-tenant information.
      call_category: call.callCategory,
      call_category_label: call.callCategoryLabel,
      caller_relationship: call.callerRelationship,
      caller_relationship_label: call.callerRelationshipLabel,
      lead_eligibility: call.leadEligibility,
      lead_eligibility_label: call.leadEligibilityLabel,
      triage_reasoning: call.triageReasoning,
      is_low_value: call.isLowValue,
      archived: call.archived,
      archived_at: call.archivedAt,
      notes: detail.notes,
      triage_history: detail.triageHistory,
      lead_grade: call.leadGrade,
      lead_score: call.leadScore,
      assistant_score: call.assistantScore,
      sentiment: call.sentiment,
      follow_up_required: call.followUpRequired,
      grade_explanation: detail.gradeExplanation,
      structured_outputs: detail.structuredOutputs,
      recommended_next_action: detail.recommendedNextAction,
      outcome: call.outcome,
      reason_for_call: structuredString(detail.structuredIntake, 'reason_for_call', 'reason'),
      urgency: structuredString(detail.structuredIntake, 'urgency'),
      callback_preference: structuredString(detail.structuredIntake, 'callback_preference'),
      structured_intake: detail.structuredIntake,
      has_issue: call.hasIssue,
    }),
  };
}

function voiceAgentRecord(agent: VoiceAgentSummary): ClientModuleRecord {
  return {
    id: agent.id,
    createdTime: agent.createdAt,
    fields: compactFields({
      'Agent Name': agent.displayName,
      avatarUrl: agent.avatarUrl,
      'Agent Profile Status': agent.status,
      // Canonical, server-derived, client-safe operational status. This is the
      // authoritative label — not the legacy mapped/not-mapped heuristic.
      'Setup Status': agent.setupStatus.clientLabel,
      'Setup Detail': agent.setupStatus.clientExplanation,
      'Agent Type': agent.isDefaultInbound ? 'Inbound Voice Agent' : 'Voice Agent',
      'Company Name (from Assigned Client)': agent.clientId,
      Visibility: 'Client Agent',
      'Connection Status': agent.mapped ? 'Connected' : 'Not mapped',
      'Agent Role': agent.purpose,
      recentCallCount: agent.metrics.recentCallCount,
      lastCallAt: agent.metrics.lastCallAt,
      followUpCount: agent.metrics.followUpCount,
      issueCount: agent.metrics.issueCount,
      statusIndicator: agent.metrics.statusIndicator,
      phoneMapped: agent.phoneMapped,
      isDefaultInbound: agent.isDefaultInbound,
      'Last Modified': agent.updatedAt,
    }),
  };
}

function structuredString(value: unknown, ...keys: string[]): string | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    const candidate = record[key];
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return undefined;
}

function compactFields(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined));
}

function extractErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  const error = record.error;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const message = (error as Record<string, unknown>).message;
    if (typeof message === 'string') return message;
  }
  const message = record.message;
  return typeof message === 'string' ? message : null;
}

function isListResponse(value: unknown, expectedTableKey: string): value is ClientModuleListResponse {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.appId === 'string' &&
    record.tableKey === expectedTableKey &&
    typeof record.total === 'number' &&
    Array.isArray(record.items) &&
    typeof record.generatedAt === 'string'
  );
}
